// src/cockpit4/components/AnnualGoalsEntry.tsx
// ============================================
// 年度目标分解入口 - 显眼展示在 CEO 工作台
//
// 功能：
//   1. 读取 Obsidian 11_Strategy（战略层）/年度目标-2026.md
//   2. 展示年度目标分解（季度 → 月度 → 周度 → 日度）
//   3. 多层级 Tab 切换：年度 / 季度 / 月度 / 周度 / 今日
//   4. 各目标可点击查看详情
//   5. 数据源全部来自 Obsidian Vault 真实文件
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target,
  Calendar,
  Loader2,
  ChevronRight,
  X,
  CheckCircle2,
  Circle,
  CircleDashed,
  Flag,
  Clock,
  AlertCircle,
  Edit,
  Save,
  FilePlus,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

interface Goal {
  id: string;
  title: string;
  category: string;
  target: string;
  progress: number;
  status: 'completed' | 'in_progress' | 'pending' | 'at_risk';
  quarter: string;
  owner?: string;
  rationale?: string;
  expectedOutcome?: string;
}

interface AnnualGoalData {
  year: string;
  vision: string;
  goals: Goal[];
  quarters: Array<{
    name: string;
    focus: string;
    progress: number;
  }>;
  source: 'obsidian' | 'default';
  filePath?: string;
}

interface DailyTask {
  time: string;
  content: string;
  completed: boolean;
  category?: string;
}

interface DailyPlan {
  date: string;
  coreGoals: string[];
  tasks: DailyTask[];
  risks: string[];
  source: 'obsidian' | 'none';
  filePath?: string;
}

type PlanLevel = 'annual' | 'quarterly' | 'monthly' | 'weekly' | 'daily';

// ============================================
// 主组件
// ============================================

export default function AnnualGoalsEntry() {
  const [data, setData] = useState<AnnualGoalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [activeQuarter, setActiveQuarter] = useState<string>('all');
  const [activeLevel, setActiveLevel] = useState<PlanLevel>('annual');
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  // 日计划数据
  const [dailyPlan, setDailyPlan] = useState<DailyPlan | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  // 年度目标原始 Markdown 内容（用于编辑模式）
  const [rawContent, setRawContent] = useState<string>('');
  // 编辑模式状态
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // 创建模板中
  const [creating, setCreating] = useState(false);

  // 加载年度目标
  const loadGoals = useCallback(async () => {
    setLoading(true);
    try {
      const paths = [
        '11_Strategy（战略层）/年度目标-2026.md',
        '11_Strategy（战略层）/Annual-Goals-2026.md',
        '10_Operations（运营层）/年度目标-2026.md',
      ];

      let content = '';
      let foundPath = '';
      for (const p of paths) {
        try {
          const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
          if (resp.ok) {
            const result = await resp.json();
            if (result.success && result.content) {
              content = result.content;
              foundPath = p;
              break;
            }
          }
        } catch {
          // 继续
        }
      }

      if (content) {
        const parsed = parseGoalsFromMarkdown(content);
        setData({ ...parsed, source: 'obsidian', filePath: foundPath });
        setRawContent(content);
      } else {
        // 无数据时设为 null（白皮书铁律二：禁止 mock 假数据）
        setData(null);
        setRawContent('');
      }
    } catch {
      // 异常时同样设为 null，不回退到 mock 数据
      setData(null);
      setRawContent('');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载今日计划
  const loadDailyPlan = useCallback(async () => {
    setDailyLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const resp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(
          `10_Operations（运营层）/Daily/${today}.md`
        )}`
      );
      if (resp.ok) {
        const result = await resp.json();
        if (result.success && result.content) {
          setDailyPlan({
            ...parseDailyPlan(result.content, today),
            source: 'obsidian',
            filePath: `10_Operations（运营层）/Daily/${today}.md`,
          });
          return;
        }
      }
      setDailyPlan({
        date: today,
        coreGoals: [],
        tasks: [],
        risks: [],
        source: 'none',
      });
    } catch {
      setDailyPlan({
        date: new Date().toISOString().slice(0, 10),
        coreGoals: [],
        tasks: [],
        risks: [],
        source: 'none',
      });
    } finally {
      setDailyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  useEffect(() => {
    if (showDetail && activeLevel === 'daily' && !dailyPlan) {
      loadDailyPlan();
    }
  }, [showDetail, activeLevel, dailyPlan, loadDailyPlan]);

  // ============================================
  // 创建年度目标模板（写入 Obsidian 后重新加载）
  // ============================================
  const handleCreateTemplate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const templatePath = '11_Strategy（战略层）/年度目标-2026.md';
      const template = `# 2026 年度目标

## 愿景
Phase III: Enterprise Asset Building（企业资产建设）

## Q1 - 基础建设
- [ ] Knowledge Factory 建设完成 30% #Knowledge 目标:500+Cards 进度:0%
- [ ] GEO+DSO 全自动获客系统上线 #Software 目标:5大AI平台 进度:0%

## Q2 - 知识飞轮
- [ ] 知识飞轮机制建立 #Knowledge 目标:软件→知识自动沉淀 进度:0%

## Q3 - CEO Brain
- [ ] CEO Brain 智能建议 #CEO 目标:每日战略建议 进度:0%

## Q4 - 规模扩张
- [ ] 待规划 #Planning 目标:待定 进度:0%
`;
      const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(templatePath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: template }),
      });
      if (!resp.ok) {
        throw new Error('写入模板失败');
      }
      // 写入成功后重新加载
      await loadGoals();
    } catch (err: any) {
      setEditError(err?.message || String(err));
    } finally {
      setCreating(false);
    }
  };

  // ============================================
  // 编辑模式：进入 / 保存 / 取消
  // ============================================
  const handleStartEdit = () => {
    setEditContent(rawContent);
    setEditError(null);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!data?.filePath || saving) return;
    setSaving(true);
    setEditError(null);
    try {
      const resp = await fetch(`/vault-api/write?path=${encodeURIComponent(data.filePath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });
      if (!resp.ok) {
        throw new Error('保存失败');
      }
      // 保存成功后退出编辑模式并重新加载
      setEditing(false);
      await loadGoals();
    } catch (err: any) {
      setEditError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredGoals = data?.goals.filter(g =>
    activeQuarter === 'all' || g.quarter === activeQuarter
  ) || [];

  const completedCount = data?.goals.filter(g => g.status === 'completed').length || 0;
  const totalCount = data?.goals.length || 0;
  const overallProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <>
      {/* ============ 年度目标概览卡片 ============ */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="relative rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.05] to-orange-500/[0.03] overflow-hidden flex-shrink-0"
      >
        {/* 装饰光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />

        <div className="relative p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <Target size={16} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-[14px] font-semibold text-white">2026 年度目标</h3>
                <p className="text-[10px] font-mono text-amber-500/80 uppercase tracking-wider flex items-center gap-1">
                  Annual Goals · Phase III Asset Building
                  {data?.source === 'obsidian' ? (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
                      <CheckCircle2 size={8} />
                      <span className="text-[8px]">Obsidian 实时</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-500/15 border border-gray-500/30 text-gray-400">
                      <AlertCircle size={8} />
                      <span className="text-[8px]">未创建</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDetail(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-md transition-colors"
            >
              查看完整分解
              <ChevronRight size={12} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={16} className="animate-spin text-amber-400" />
            </div>
          ) : data ? (
            <>
              {/* 总进度 */}
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-gray-500 uppercase">总进度</span>
                  <span className="text-[20px] font-mono font-bold text-amber-300">{overallProgress}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-gray-500 uppercase">已完成</span>
                  <span className="text-[14px] font-mono font-bold text-emerald-400">{completedCount}</span>
                  <span className="text-[10px] font-mono text-gray-600">/ {totalCount}</span>
                </div>
                <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${overallProgress}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                </div>
              </div>

              {/* 季度概览 - 可点击切换筛选 */}
              <div className="grid grid-cols-4 gap-2">
                {data.quarters.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setActiveQuarter(q.name);
                      setActiveLevel('quarterly');
                      setShowDetail(true);
                    }}
                    className="text-left rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5 hover:bg-amber-500/[0.04] hover:border-amber-500/20 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-mono text-gray-400 uppercase">{q.name}</span>
                      <span className="text-[11px] font-mono font-bold text-amber-300">{q.progress}%</span>
                    </div>
                    <div className="text-[9px] text-gray-500 truncate">{q.focus}</div>
                    <div className="mt-1 h-0.5 bg-white/[0.04] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500/60"
                        style={{ width: `${q.progress}%` }}
                      />
                    </div>
                  </button>
                ))}
              </div>

              {/* Top 3 高优先级目标 - 可点击查看详情 */}
              <div className="mt-3 space-y-1">
                {data.goals.slice(0, 3).map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setSelectedGoal(goal)}
                    className="w-full flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-white/[0.04] transition-colors text-left cursor-pointer"
                  >
                    {goal.status === 'completed' ? (
                      <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
                    ) : goal.status === 'in_progress' ? (
                      <CircleDashed size={12} className="text-amber-400 flex-shrink-0" />
                    ) : goal.status === 'at_risk' ? (
                      <Flag size={12} className="text-red-400 flex-shrink-0" />
                    ) : (
                      <Circle size={12} className="text-gray-600 flex-shrink-0" />
                    )}
                    <span className="text-[11px] text-gray-200 flex-1 truncate">{goal.title}</span>
                    <span className="text-[9px] font-mono text-gray-500">{goal.category}</span>
                    <span className="text-[10px] font-mono text-amber-400">{goal.progress}%</span>
                    <ChevronRight size={10} className="text-gray-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* 暂无数据提示卡片（白皮书铁律二：禁止 mock 假数据） */
            <div className="rounded-md border border-dashed border-amber-500/20 bg-amber-500/[0.02] p-4 text-center">
              <AlertCircle size={20} className="text-amber-400/60 mx-auto mb-2" />
              <p className="text-[12px] text-gray-300 font-medium mb-1">暂无年度目标数据</p>
              <p className="text-[10px] text-gray-500 mb-3">
                请在 Obsidian 的 11_Strategy（战略层）/年度目标-2026.md 中创建年度目标
              </p>
              <button
                onClick={handleCreateTemplate}
                disabled={creating}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <FilePlus size={12} />}
                {creating ? '创建中...' : '创建年度目标模板'}
              </button>
              {editError && (
                <p className="text-[10px] text-red-400 mt-2">❌ {editError}</p>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* ============ 年度目标详细分解 Modal ============ */}
      <AnimatePresence>
        {showDetail && data && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetail(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl border border-amber-500/20 bg-[#0a0e1a] overflow-hidden"
            >
              {/* 头部 */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] bg-amber-500/[0.03]">
                <div className="flex items-center gap-2">
                  <Target size={16} className="text-amber-400" />
                  <h3 className="text-[15px] font-semibold text-white">2026 战略计划分解</h3>
                  <span className="text-[10px] font-mono text-gray-500">
                    {data.vision}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* 编辑 / 保存 / 取消 按钮 */}
                  {!editing ? (
                    <button
                      onClick={handleStartEdit}
                      disabled={!data.filePath}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="编辑年度目标 Markdown 源文件"
                    >
                      <Edit size={11} />
                      编辑
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                        {saving ? '保存中...' : '保存'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-mono text-gray-400 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.1] rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        取消
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowDetail(false)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* 层级 Tab：年度 / 季度 / 月度 / 周度 / 今日 */}
              <div className="flex items-center gap-1 px-5 py-2 border-b border-white/[0.04] overflow-x-auto">
                {([
                  { key: 'annual', label: '年度', icon: Target },
                  { key: 'quarterly', label: '季度', icon: Calendar },
                  { key: 'monthly', label: '月度', icon: Calendar },
                  { key: 'weekly', label: '周度', icon: Calendar },
                  { key: 'daily', label: '今日', icon: Clock },
                ] as const).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveLevel(key)}
                    className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-mono rounded transition-colors whitespace-nowrap ${
                      activeLevel === key
                        ? 'text-white bg-amber-500/15 border border-amber-500/30'
                        : 'text-gray-500 hover:text-gray-300 border border-transparent'
                    }`}
                  >
                    <Icon size={10} />
                    {label}
                  </button>
                ))}
                {data.filePath && (
                  <span className="ml-auto flex items-center gap-1 text-[9px] font-mono text-emerald-400/80">
                    <CheckCircle2 size={9} />
                    {data.filePath}
                  </span>
                )}
              </div>

              {/* 内容区 */}
              <div className="flex-1 overflow-y-auto p-4">
                {editing ? (
                  /* 编辑模式：textarea 编辑 Markdown 源文件 */
                  <div className="flex flex-col h-full">
                    {editError && (
                      <div className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/20">
                        <AlertCircle size={11} className="text-red-400 flex-shrink-0" />
                        <span className="text-[10px] text-red-300">{editError}</span>
                      </div>
                    )}
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      disabled={saving}
                      className="flex-1 w-full min-h-[400px] bg-[#0d1117] border border-white/[0.08] rounded-md p-3 text-[12px] font-mono text-gray-200 outline-none focus:border-amber-500/40 resize-none disabled:opacity-60"
                      placeholder="编辑年度目标 Markdown 内容..."
                      spellCheck={false}
                    />
                    <p className="text-[9px] font-mono text-gray-600 mt-1">
                      保存后将写回 Obsidian：{data.filePath}
                    </p>
                  </div>
                ) : (
                  <>
                    {activeLevel === 'annual' && (
                      <AnnualLevelView
                        data={data}
                        activeQuarter={activeQuarter}
                        setActiveQuarter={setActiveQuarter}
                        onSelectGoal={setSelectedGoal}
                      />
                    )}

                    {activeLevel === 'quarterly' && (
                      <QuarterlyLevelView
                        data={data}
                        activeQuarter={activeQuarter}
                        setActiveQuarter={setActiveQuarter}
                        onSelectGoal={setSelectedGoal}
                      />
                    )}

                    {activeLevel === 'monthly' && (
                      <MonthlyLevelView data={data} onSelectGoal={setSelectedGoal} />
                    )}

                    {activeLevel === 'weekly' && (
                      <WeeklyLevelView data={data} onSelectGoal={setSelectedGoal} />
                    )}

                    {activeLevel === 'daily' && (
                      <DailyLevelView
                        plan={dailyPlan}
                        loading={dailyLoading}
                        onReload={loadDailyPlan}
                      />
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ 单个目标详情 Modal ============ */}
      <AnimatePresence>
        {selectedGoal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedGoal(null)}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-xl border border-amber-500/30 bg-[#0a0e1a] overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {selectedGoal.status === 'completed' ? (
                      <CheckCircle2 size={16} className="text-emerald-400" />
                    ) : selectedGoal.status === 'in_progress' ? (
                      <CircleDashed size={16} className="text-amber-400" />
                    ) : selectedGoal.status === 'at_risk' ? (
                      <Flag size={16} className="text-red-400" />
                    ) : (
                      <Circle size={16} className="text-gray-600" />
                    )}
                    <h3 className="text-[14px] font-semibold text-white">
                      {selectedGoal.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => setSelectedGoal(null)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500 font-mono w-16">季度：</span>
                    <span className="text-amber-300 font-mono">{selectedGoal.quarter}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500 font-mono w-16">分类：</span>
                    <span className="px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 font-mono text-[10px]">
                      {selectedGoal.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500 font-mono w-16">目标：</span>
                    <span className="text-gray-200">{selectedGoal.target}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500 font-mono w-16">进度：</span>
                    <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          selectedGoal.status === 'completed' ? 'bg-emerald-500' :
                          selectedGoal.status === 'at_risk' ? 'bg-red-500' :
                          'bg-amber-500'
                        }`}
                        style={{ width: `${selectedGoal.progress}%` }}
                      />
                    </div>
                    <span className="text-amber-300 font-mono font-bold">{selectedGoal.progress}%</span>
                  </div>
                  {selectedGoal.owner && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-gray-500 font-mono w-16">负责人：</span>
                      <span className="text-gray-200">{selectedGoal.owner}</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ============================================
// 年度视图
// ============================================

function AnnualLevelView({
  data,
  activeQuarter,
  setActiveQuarter,
  onSelectGoal,
}: {
  data: AnnualGoalData;
  activeQuarter: string;
  setActiveQuarter: (q: string) => void;
  onSelectGoal: (g: Goal) => void;
}) {
  const filtered = activeQuarter === 'all'
    ? data.goals
    : data.goals.filter(g => g.quarter === activeQuarter);

  return (
    <div className="space-y-3">
      {/* 季度筛选 */}
      <div className="flex items-center gap-1 flex-wrap">
        <button
          onClick={() => setActiveQuarter('all')}
          className={`px-2.5 py-1 text-[10px] font-mono rounded transition-colors ${
            activeQuarter === 'all' ? 'text-white bg-white/[0.04]' : 'text-gray-500 hover:text-gray-300'
          }`}
        >
          全部 ({data.goals.length})
        </button>
        {data.quarters.map((q) => (
          <button
            key={q.name}
            onClick={() => setActiveQuarter(q.name)}
            className={`px-2.5 py-1 text-[10px] font-mono rounded transition-colors ${
              activeQuarter === q.name ? 'text-white bg-white/[0.04]' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {q.name} ({data.goals.filter(g => g.quarter === q.name).length})
          </button>
        ))}
      </div>

      {/* 目标列表 */}
      <div className="space-y-2">
        {filtered.map((goal) => (
          <button
            key={goal.id}
            onClick={() => onSelectGoal(goal)}
            className="w-full text-left rounded-md border border-white/[0.04] bg-white/[0.01] p-3 hover:border-amber-500/20 hover:bg-amber-500/[0.02] transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {goal.status === 'completed' ? (
                  <CheckCircle2 size={13} className="text-emerald-400" />
                ) : goal.status === 'in_progress' ? (
                  <CircleDashed size={13} className="text-amber-400" />
                ) : goal.status === 'at_risk' ? (
                  <Flag size={13} className="text-red-400" />
                ) : (
                  <Circle size={13} className="text-gray-600" />
                )}
                <span className="text-[12px] text-gray-200 font-medium">{goal.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-mono text-gray-500 px-1.5 py-0.5 rounded bg-white/[0.04]">
                  {goal.category}
                </span>
                <span className="text-[9px] font-mono text-amber-500/80">{goal.quarter}</span>
                <ChevronRight size={11} className="text-gray-600" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-mono text-gray-500">目标：{goal.target}</span>
              <div className="flex-1 h-1 bg-white/[0.04] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    goal.status === 'completed' ? 'bg-emerald-500' :
                    goal.status === 'at_risk' ? 'bg-red-500' :
                    'bg-amber-500'
                  }`}
                  style={{ width: `${goal.progress}%` }}
                />
              </div>
              <span className="text-[11px] font-mono font-bold text-amber-300">{goal.progress}%</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 季度视图（按季度分组）
// ============================================

function QuarterlyLevelView({
  data,
  activeQuarter,
  setActiveQuarter,
  onSelectGoal,
}: {
  data: AnnualGoalData;
  activeQuarter: string;
  setActiveQuarter: (q: string) => void;
  onSelectGoal: (g: Goal) => void;
}) {
  return (
    <div className="space-y-4">
      {data.quarters.map((q) => {
        const qGoals = data.goals.filter(g => g.quarter === q.name);
        const qCompleted = qGoals.filter(g => g.status === 'completed').length;
        return (
          <div
            key={q.name}
            className={`rounded-lg border transition-colors ${
              activeQuarter === q.name
                ? 'border-amber-500/30 bg-amber-500/[0.03]'
                : 'border-white/[0.04] bg-white/[0.01]'
            }`}
          >
            <button
              onClick={() => setActiveQuarter(activeQuarter === q.name ? 'all' : q.name)}
              className="w-full flex items-center justify-between px-3 py-2 text-left cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Calendar size={12} className="text-amber-400" />
                <span className="text-[12px] font-semibold text-white">{q.name}</span>
                <span className="text-[10px] text-gray-500">{q.focus}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-emerald-400">{qCompleted}/{qGoals.length}</span>
                <span className="text-[11px] font-mono font-bold text-amber-300">{q.progress}%</span>
              </div>
            </button>
            {activeQuarter === q.name && (
              <div className="px-3 pb-2 space-y-1">
                {qGoals.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => onSelectGoal(goal)}
                    className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03] transition-colors text-left cursor-pointer"
                  >
                    {goal.status === 'completed' ? (
                      <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                    ) : (
                      <CircleDashed size={11} className="text-amber-400 flex-shrink-0" />
                    )}
                    <span className="text-[11px] text-gray-200 flex-1 truncate">{goal.title}</span>
                    <span className="text-[10px] font-mono text-amber-400">{goal.progress}%</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// 月度视图（读取 Obsidian 真实月度计划文件）
// ============================================

interface MonthlyPlan {
  coreGoals: string[];
  tasks: { content: string; completed: boolean }[];
  filePath?: string;
  source: 'obsidian' | 'none';
}

function MonthlyLevelView({
  data,
  onSelectGoal,
}: {
  data: AnnualGoalData;
  onSelectGoal: (g: Goal) => void;
}) {
  const quarterMonths: Record<string, string[]> = {
    'Q1': ['1月', '2月', '3月'],
    'Q2': ['4月', '5月', '6月'],
    'Q3': ['7月', '8月', '9月'],
    'Q4': ['10月', '11月', '12月'],
  };

  const currentMonth = new Date().getMonth() + 1;
  const currentMonthLabel = `${currentMonth}月`;
  const yearMonth = `${new Date().getFullYear()}-${String(currentMonth).padStart(2, '0')}`;

  const [monthlyFiles, setMonthlyFiles] = useState<string[]>([]);
  const [currentMonthPlan, setCurrentMonthPlan] = useState<MonthlyPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadMonthly = async () => {
      setLoading(true);
      try {
        // 1. 读取月度计划文件列表
        const listResp = await fetch(
          `/vault-api/list?dir=${encodeURIComponent('10_Operations（运营层）/Monthly/')}&ext=.md`
        );
        let files: { name: string; path: string }[] = [];
        if (listResp.ok) {
          const result = await listResp.json();
          if (result.success) {
            files = result.files || [];
          }
        }
        if (cancelled) return;
        setMonthlyFiles(files.map((f) => f.name));

        // 2. 找到当月计划文件并读取内容
        const currentMonthFile = files.find((f) =>
          f.name.includes(yearMonth) || f.name.includes(`${currentMonth}月`)
        );

        if (currentMonthFile) {
          const readResp = await fetch(
            `/vault-api/read?path=${encodeURIComponent(currentMonthFile.path)}`
          );
          if (readResp.ok) {
            const readResult = await readResp.json();
            if (readResult.content) {
              const parsed = parseMonthlyPlan(readResult.content);
              if (cancelled) return;
              setCurrentMonthPlan({
                coreGoals: parsed.coreGoals,
                tasks: parsed.tasks,
                filePath: currentMonthFile.path,
                source: 'obsidian',
              });
              return;
            }
          }
        }
        if (!cancelled) {
          setCurrentMonthPlan({
            coreGoals: [],
            tasks: [],
            source: 'none',
          });
        }
      } catch {
        if (!cancelled) {
          setCurrentMonthPlan({
            coreGoals: [],
            tasks: [],
            source: 'none',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadMonthly();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <div className="px-3 py-2 rounded-md border border-blue-500/20 bg-blue-500/[0.04] flex items-center gap-2">
        <Clock size={12} className="text-blue-400" />
        <span className="text-[11px] text-gray-300">
          当前月份：<span className="text-blue-300 font-mono font-bold">{currentMonthLabel}</span>
          <span className="text-gray-500 ml-2">
            {currentMonthPlan?.source === 'obsidian'
              ? `数据来源：Obsidian · ${currentMonthPlan.filePath}`
              : '月度计划基于季度目标分解'}
          </span>
        </span>
      </div>

      {/* 当月真实计划内容 */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={16} className="animate-spin text-blue-400" />
          <span className="ml-2 text-[11px] font-mono text-gray-500">加载月度计划...</span>
        </div>
      ) : currentMonthPlan && currentMonthPlan.source === 'obsidian' ? (
        <div className="space-y-2">
          {/* 核心目标 */}
          {currentMonthPlan.coreGoals.length > 0 && (
            <div className="rounded-md border border-blue-500/20 bg-blue-500/[0.03] p-3">
              <div className="text-[10px] font-mono text-blue-400/80 uppercase tracking-wider mb-2">
                本月核心目标
              </div>
              <div className="space-y-1">
                {currentMonthPlan.coreGoals.map((goal, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[13px] font-mono font-bold text-blue-300">{i + 1}.</span>
                    <span className="text-[12px] text-gray-200">{goal}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* 任务清单 */}
          {currentMonthPlan.tasks.length > 0 && (
            <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-3">
              <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                本月任务清单
              </div>
              <div className="space-y-1">
                {currentMonthPlan.tasks.map((task, i) => (
                  <div key={i} className="flex items-center gap-2 py-0.5 px-2 rounded hover:bg-white/[0.02]">
                    {task.completed ? (
                      <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle size={11} className="text-gray-600 flex-shrink-0" />
                    )}
                    <span className={`text-[11px] ${task.completed ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                      {task.content}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {currentMonthPlan.coreGoals.length === 0 && currentMonthPlan.tasks.length === 0 && (
            <div className="text-center py-6 text-[11px] font-mono text-gray-500">
              月度计划文件已存在，但未解析到核心目标或任务
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <Calendar size={24} className="text-gray-600 mx-auto mb-2" />
          <p className="text-[12px] text-gray-400 mb-1">本月暂无计划文件，请先生成</p>
          <p className="text-[10px] font-mono text-gray-600">
            路径：10_Operations（运营层）/Monthly/{yearMonth}.md
          </p>
        </div>
      )}

      {/* 季度月份概览（标注哪些月份有计划文件） */}
      {data.quarters.map((q) => {
        const qGoals = data.goals.filter(g => g.quarter === q.name);
        return (
          <div key={q.name} className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar size={12} className="text-amber-400" />
                <span className="text-[12px] font-semibold text-white">{q.name}</span>
                <span className="text-[10px] text-gray-500">{q.focus}</span>
              </div>
              <span className="text-[10px] font-mono text-gray-500">{q.progress}%</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {quarterMonths[q.name]?.map((month) => {
                const isCurrent = month === currentMonthLabel;
                const monthNum = parseInt(month);
                const monthFilesForThis = monthlyFiles.filter((name) =>
                  name.includes(`${String(monthNum).padStart(2, '0')}月`) ||
                  name.includes(`-${String(monthNum).padStart(2, '0')}`)
                );
                const hasFile = monthFilesForThis.length > 0;
                return (
                  <div
                    key={month}
                    className={`rounded-md border p-2 ${
                      isCurrent
                        ? 'border-blue-500/40 bg-blue-500/[0.05]'
                        : 'border-white/[0.04] bg-white/[0.01]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] font-mono ${isCurrent ? 'text-blue-300' : 'text-gray-400'}`}>
                        {month}
                      </span>
                      {isCurrent && (
                        <span className="text-[8px] font-mono text-blue-400 uppercase">本月</span>
                      )}
                      {!isCurrent && hasFile && (
                        <CheckCircle2 size={8} className="text-emerald-400" />
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {qGoals.slice(0, 2).map((goal) => (
                        <button
                          key={goal.id}
                          onClick={() => onSelectGoal(goal)}
                          className="w-full flex items-center gap-1 text-left hover:bg-white/[0.03] rounded px-1 py-0.5 cursor-pointer"
                        >
                          {goal.status === 'completed' ? (
                            <CheckCircle2 size={8} className="text-emerald-400 flex-shrink-0" />
                          ) : (
                            <CircleDashed size={8} className="text-amber-400 flex-shrink-0" />
                          )}
                          <span className="text-[9px] text-gray-400 truncate">{goal.title}</span>
                        </button>
                      ))}
                      {qGoals.length === 0 && (
                        <span className="text-[9px] text-gray-600">暂无目标</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// 周度视图（读取 Obsidian 真实周度计划文件）
// ============================================

interface WeeklyPlan {
  coreGoals: string[];
  /** 按天分组的任务，key 为 '周一'..'周日' */
  dayTasks: Record<string, { content: string; completed: boolean }[]>;
  filePath?: string;
  source: 'obsidian' | 'none';
}

function WeeklyLevelView({
  data,
  onSelectGoal,
}: {
  data: AnnualGoalData;
  onSelectGoal: (g: Goal) => void;
}) {
  // 计算当前周
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((now.getTime() - yearStart.getTime()) / 86400000 + yearStart.getDay() + 1) / 7);

  // 计算本周周一的日期字符串（用于匹配文件名）
  const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayOfWeek + 1);
  const mondayStr = monday.toISOString().slice(0, 10);

  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadWeekly = async () => {
      setLoading(true);
      try {
        // 1. 读取周度计划文件列表
        const listResp = await fetch(
          `/vault-api/list?dir=${encodeURIComponent('10_Operations（运营层）/Weekly/')}&ext=.md`
        );
        let files: { name: string; path: string }[] = [];
        if (listResp.ok) {
          const result = await listResp.json();
          if (result.success) {
            files = result.files || [];
          }
        }

        // 2. 找到本周计划文件（按周次或周一日期匹配）
        const weekStr = String(weekNum).padStart(2, '0');
        const currentWeekFile = files.find((f) =>
          f.name.includes(`W${weekNum}`) ||
          f.name.includes(`W${weekStr}`) ||
          f.name.includes(`第${weekNum}周`) ||
          f.name.includes(mondayStr) ||
          f.name.includes(`${now.getFullYear()}-第${weekNum}周`)
        );

        if (currentWeekFile) {
          const readResp = await fetch(
            `/vault-api/read?path=${encodeURIComponent(currentWeekFile.path)}`
          );
          if (readResp.ok) {
            const readResult = await readResp.json();
            if (readResult.content) {
              const parsed = parseWeeklyPlan(readResult.content);
              if (cancelled) return;
              setWeeklyPlan({
                coreGoals: parsed.coreGoals,
                dayTasks: parsed.dayTasks,
                filePath: currentWeekFile.path,
                source: 'obsidian',
              });
              return;
            }
          }
        }
        if (!cancelled) {
          setWeeklyPlan({
            coreGoals: [],
            dayTasks: {},
            source: 'none',
          });
        }
      } catch {
        if (!cancelled) {
          setWeeklyPlan({
            coreGoals: [],
            dayTasks: {},
            source: 'none',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadWeekly();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayNames = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const hasRealPlan = weeklyPlan?.source === 'obsidian';

  return (
    <div className="space-y-3">
      <div className="px-3 py-2 rounded-md border border-purple-500/20 bg-purple-500/[0.04] flex items-center gap-2">
        <Clock size={12} className="text-purple-400" />
        <span className="text-[11px] text-gray-300">
          当前周次：<span className="text-purple-300 font-mono font-bold">第 {weekNum} 周</span>
          <span className="text-gray-500 ml-2">
            {hasRealPlan
              ? `数据来源：Obsidian · ${weeklyPlan?.filePath}`
              : '本周应推进以下目标'}
          </span>
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={16} className="animate-spin text-purple-400" />
          <span className="ml-2 text-[11px] font-mono text-gray-500">加载周度计划...</span>
        </div>
      ) : hasRealPlan ? (
        <>
          {/* 本周核心目标 */}
          {weeklyPlan!.coreGoals.length > 0 && (
            <div className="rounded-md border border-purple-500/20 bg-purple-500/[0.03] p-3">
              <div className="text-[10px] font-mono text-purple-400/80 uppercase tracking-wider mb-2">
                本周核心目标
              </div>
              <div className="space-y-1">
                {weeklyPlan!.coreGoals.map((goal, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[13px] font-mono font-bold text-purple-300">{i + 1}.</span>
                    <span className="text-[12px] text-gray-200">{goal}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7 天任务网格（真实数据） */}
          <div className="grid grid-cols-7 gap-1.5">
            {dayNames.map((day, i) => {
              const today = new Date();
              const dow = today.getDay() === 0 ? 7 : today.getDay();
              const isToday = i + 1 === dow;
              const tasks = weeklyPlan!.dayTasks[day] || [];
              return (
                <div
                  key={day}
                  className={`rounded-md border p-2 min-h-[80px] ${
                    isToday
                      ? 'border-purple-500/40 bg-purple-500/[0.05]'
                      : 'border-white/[0.04] bg-white/[0.01]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-mono ${isToday ? 'text-purple-300' : 'text-gray-400'}`}>
                      {day}
                    </span>
                    {isToday && (
                      <span className="text-[8px] font-mono text-purple-400 uppercase">今日</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {tasks.map((task, j) => (
                      <div key={j} className="flex items-start gap-1 px-1 py-0.5">
                        {task.completed ? (
                          <CheckCircle2 size={7} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Circle size={7} className="text-gray-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`text-[8px] ${task.completed ? 'text-gray-600 line-through' : 'text-gray-300'} break-all`}>
                          {task.content}
                        </span>
                      </div>
                    ))}
                    {tasks.length === 0 && (
                      <span className="text-[8px] text-gray-700">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* 无真实文件提示 */}
          <div className="text-center py-6">
            <Calendar size={24} className="text-gray-600 mx-auto mb-2" />
            <p className="text-[12px] text-gray-400 mb-1">本周暂无计划文件</p>
            <p className="text-[10px] font-mono text-gray-600">
              路径：10_Operations（运营层）/Weekly/ （第 {weekNum} 周，周一 {mondayStr}）
            </p>
          </div>

          {/* 降级显示：年度目标中本周推进项 */}
          <div className="grid grid-cols-7 gap-1.5">
            {dayNames.map((day, i) => {
              const today = new Date();
              const dow = today.getDay() === 0 ? 7 : today.getDay();
              const isToday = i + 1 === dow;
              return (
                <div
                  key={day}
                  className={`rounded-md border p-2 min-h-[80px] ${
                    isToday
                      ? 'border-purple-500/40 bg-purple-500/[0.05]'
                      : 'border-white/[0.04] bg-white/[0.01]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-mono ${isToday ? 'text-purple-300' : 'text-gray-400'}`}>
                      {day}
                    </span>
                    {isToday && (
                      <span className="text-[8px] font-mono text-purple-400 uppercase">今日</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {data.goals.filter(g => g.status === 'in_progress').slice(0, 2).map((goal) => (
                      <button
                        key={goal.id}
                        onClick={() => onSelectGoal(goal)}
                        className="w-full flex items-center gap-1 text-left hover:bg-white/[0.03] rounded px-1 py-0.5 cursor-pointer"
                      >
                        <CircleDashed size={7} className="text-amber-400 flex-shrink-0" />
                        <span className="text-[8px] text-gray-400 truncate">{goal.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 本周推进目标（年度目标降级显示） */}
      <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-3">
        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
          本周推进目标
        </div>
        <div className="space-y-1">
          {data.goals.filter(g => g.status === 'in_progress').slice(0, 5).map((goal) => (
            <button
              key={goal.id}
              onClick={() => onSelectGoal(goal)}
              className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03] transition-colors text-left cursor-pointer"
            >
              <CircleDashed size={11} className="text-amber-400 flex-shrink-0" />
              <span className="text-[11px] text-gray-200 flex-1 truncate">{goal.title}</span>
              <span className="text-[9px] font-mono text-gray-500">{goal.quarter}</span>
              <span className="text-[10px] font-mono text-amber-400">{goal.progress}%</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 今日视图
// ============================================

function DailyLevelView({
  plan,
  loading,
  onReload,
}: {
  plan: DailyPlan | null;
  loading: boolean;
  onReload: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={16} className="animate-spin text-amber-400" />
        <span className="ml-2 text-[11px] font-mono text-gray-500">加载今日计划...</span>
      </div>
    );
  }

  if (!plan || plan.source === 'none') {
    return (
      <div className="text-center py-8">
        <Clock size={24} className="text-gray-600 mx-auto mb-2" />
        <p className="text-[12px] text-gray-400 mb-1">今日尚未生成日计划</p>
        <p className="text-[10px] font-mono text-gray-600 mb-3">
          可在底部"战略计划与闭环审核"中生成今日计划
        </p>
        <button
          onClick={onReload}
          className="px-3 py-1.5 text-[11px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded transition-colors"
        >
          重新加载
        </button>
      </div>
    );
  }

  const completedTasks = plan.tasks.filter(t => t.completed).length;

  return (
    <div className="space-y-3">
      {/* 头部 */}
      <div className="px-3 py-2 rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={12} className="text-emerald-400" />
          <span className="text-[11px] text-gray-300">
            今日计划：<span className="text-emerald-300 font-mono font-bold">{plan.date}</span>
          </span>
          <span className="text-[10px] font-mono text-emerald-400">
            {completedTasks}/{plan.tasks.length} 已完成
          </span>
        </div>
        {plan.filePath && (
          <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-400/80">
            <CheckCircle2 size={9} />
            Obsidian 实时
          </span>
        )}
      </div>

      {/* 核心目标 */}
      {plan.coreGoals.length > 0 && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/[0.03] p-3">
          <div className="text-[10px] font-mono text-amber-400/80 uppercase tracking-wider mb-2">
            今日核心目标
          </div>
          <div className="space-y-1">
            {plan.coreGoals.map((goal, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[14px] font-mono font-bold text-amber-300">{i + 1}.</span>
                <span className="text-[12px] text-gray-200">{goal}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 任务清单 */}
      {plan.tasks.length > 0 && (
        <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-3">
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
            任务清单
          </div>
          <div className="space-y-1">
            {plan.tasks.map((task, i) => (
              <div
                key={i}
                className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white/[0.02]"
              >
                {task.completed ? (
                  <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
                ) : (
                  <Circle size={12} className="text-gray-600 flex-shrink-0" />
                )}
                <span className="text-[10px] font-mono text-gray-500 w-12">{task.time}</span>
                <span className={`text-[12px] flex-1 ${task.completed ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                  {task.content}
                </span>
                {task.category && (
                  <span className="text-[9px] font-mono text-gray-500 px-1.5 py-0.5 rounded bg-white/[0.04]">
                    {task.category}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 风险 */}
      {plan.risks.length > 0 && (
        <div className="rounded-md border border-red-500/20 bg-red-500/[0.03] p-3">
          <div className="text-[10px] font-mono text-red-400/80 uppercase tracking-wider mb-2">
            风险与待办
          </div>
          <div className="space-y-1">
            {plan.risks.map((risk, i) => (
              <div key={i} className="flex items-start gap-2">
                <AlertCircle size={11} className="text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-[12px] text-gray-300">{risk}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onReload}
        className="w-full py-2 text-[11px] font-mono text-gray-500 hover:text-gray-300 border border-white/[0.04] hover:border-white/[0.08] rounded transition-colors"
      >
        重新加载今日计划
      </button>
    </div>
  );
}

// ============================================
// 解析年度目标 Markdown
// ============================================

function parseGoalsFromMarkdown(content: string): AnnualGoalData {
  const goals: Goal[] = [];
  const lines = content.split('\n');
  let currentQuarter = 'Q1';

  // 提取愿景
  let vision = 'Phase III: Enterprise Asset Building';
  const visionMatch = content.match(/>\s*\*\*愿景\*\*[：:]\s*(.+)/);
  if (visionMatch) vision = visionMatch[1].trim();

  const quarterMatch = content.match(/Q([1-4])/i);
  if (quarterMatch) currentQuarter = `Q${quarterMatch[1]}`;

  for (const line of lines) {
    // 匹配季度标题
    const qMatch = line.match(/^#+\s*Q([1-4])/i);
    if (qMatch) {
      currentQuarter = `Q${qMatch[1]}`;
      continue;
    }

    // 匹配目标条目：- [x] 目标名 (进度: 50%) [Knowledge]
    const goalMatch = line.match(/^-\s*\[([x\s])\]\s*(.+?)(?:\s*\(进度[：:]\s*(\d+)%\))?(?:\s*\[([^\]]+)\])?$/);
    if (goalMatch) {
      const completed = goalMatch[1] === 'x';
      const title = goalMatch[2].trim();
      const progress = goalMatch[3] ? parseInt(goalMatch[3]) : completed ? 100 : 0;
      const category = goalMatch[4] || '通用';

      goals.push({
        id: `goal-${goals.length}`,
        title,
        category,
        target: title,
        progress,
        status: completed ? 'completed' : progress > 0 ? 'in_progress' : 'pending',
        quarter: currentQuarter,
      });
    }
  }

  // 计算季度进度
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'].map(name => {
    const qGoals = goals.filter(g => g.quarter === name);
    const completed = qGoals.filter(g => g.status === 'completed').length;
    // 提取季度焦点
    const focusMatch = content.match(new RegExp(`##\\s*${name}[^\\n]*\\n\\s*\\*\\*焦点\\*\\*[：:]\\s*([^\\n]+)`));
    const focus = focusMatch ? focusMatch[1].trim() : (qGoals[0]?.category || '—');
    return {
      name,
      focus,
      progress: qGoals.length > 0 ? Math.round((completed / qGoals.length) * 100) : 0,
    };
  });

  return {
    year: '2026',
    vision,
    goals,
    quarters,
    source: 'default',
  };
}

// ============================================
// 解析今日计划 Markdown
// ============================================

function parseDailyPlan(content: string, date: string): Omit<DailyPlan, 'source' | 'filePath'> {
  const coreGoals: string[] = [];
  const tasks: DailyTask[] = [];
  const risks: string[] = [];

  const lines = content.split('\n');
  let inCoreGoals = false;
  let inTasks = false;
  let inRisks = false;

  for (const line of lines) {
    // 核心目标段落
    if (/核心目标|今日核心/.test(line)) {
      inCoreGoals = true;
      inTasks = false;
      inRisks = false;
      continue;
    }
    // 任务清单段落
    if (/任务清单|今日任务/.test(line)) {
      inCoreGoals = false;
      inTasks = true;
      inRisks = false;
      continue;
    }
    // 风险段落
    if (/风险|待办/.test(line)) {
      inCoreGoals = false;
      inTasks = false;
      inRisks = true;
      continue;
    }
    // 段落结束
    if (/^##\s/.test(line)) {
      inCoreGoals = false;
      inTasks = false;
      inRisks = false;
      continue;
    }

    // 提取核心目标
    if (inCoreGoals) {
      const m = line.match(/^\d+\.\s*\*\*(.+?)\*\*/);
      if (m) coreGoals.push(m[1].trim());
    }

    // 提取任务
    if (inTasks) {
      const m = line.match(/^-\s*\[([x\s])\]\s*(?:(\d{2}:\d{2}))?\s*(.+)$/);
      if (m) {
        tasks.push({
          time: m[2] || '—',
          content: m[3].trim(),
          completed: m[1] === 'x',
        });
      }
    }

    // 提取风险
    if (inRisks) {
      const m = line.match(/^-\s*(?:⚠️|📌)?\s*(.+)$/);
      if (m) risks.push(m[1].trim());
    }
  }

  return {
    date,
    coreGoals,
    tasks,
    risks,
  };
}

// ============================================
// 解析月度计划 Markdown
// ============================================

function parseMonthlyPlan(content: string): {
  coreGoals: string[];
  tasks: { content: string; completed: boolean }[];
} {
  const coreGoals: string[] = [];
  const tasks: { content: string; completed: boolean }[] = [];

  const lines = content.split('\n');
  let inCoreGoals = false;
  let inTasks = false;

  for (const line of lines) {
    // 核心目标段落
    if (/核心目标|月度目标|本月目标/.test(line)) {
      inCoreGoals = true;
      inTasks = false;
      continue;
    }
    // 任务清单段落
    if (/任务清单|本月任务|关键任务/.test(line)) {
      inCoreGoals = false;
      inTasks = true;
      continue;
    }
    // 段落结束（二级标题）
    if (/^##\s/.test(line)) {
      inCoreGoals = false;
      inTasks = false;
      continue;
    }

    // 提取核心目标（支持 "1. **目标**" 或 "1. 目标"）
    if (inCoreGoals) {
      const mBold = line.match(/^\d+\.\s*\*\*(.+?)\*\*/);
      if (mBold) {
        coreGoals.push(mBold[1].trim());
      } else {
        const mPlain = line.match(/^\d+\.\s*(.+)/);
        if (mPlain) coreGoals.push(mPlain[1].trim());
      }
    }

    // 提取任务（- [ ] / - [x]）
    if (inTasks) {
      const m = line.match(/^-\s*\[([x\s])\]\s*(.+)$/);
      if (m) {
        tasks.push({
          content: m[2].trim(),
          completed: m[1] === 'x',
        });
      }
    }
  }

  return { coreGoals, tasks };
}

// ============================================
// 解析周度计划 Markdown（按天分组提取任务）
// ============================================

function parseWeeklyPlan(content: string): {
  coreGoals: string[];
  dayTasks: Record<string, { content: string; completed: boolean }[]>;
} {
  const coreGoals: string[] = [];
  const dayTasks: Record<string, { content: string; completed: boolean }[]> = {};

  const dayNames = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const dayNamesAlt = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const allDayNames = [...dayNames, ...dayNamesAlt];

  // 初始化每天的空数组
  for (const day of allDayNames) {
    dayTasks[day] = [];
  }

  const lines = content.split('\n');
  let inCoreGoals = false;
  let currentDay: string | null = null;

  for (const line of lines) {
    // 核心目标段落
    if (/核心目标|本周目标|周目标/.test(line) && /^##/.test(line)) {
      inCoreGoals = true;
      currentDay = null;
      continue;
    }

    // 检查是否为日期标题（### 周一 / ## Monday 等）
    const dayHeaderMatch = line.match(/^#+\s*(.+)/);
    if (dayHeaderMatch) {
      const headerText = dayHeaderMatch[1].trim();
      inCoreGoals = false;
      const foundDay = allDayNames.find((d) => headerText.includes(d));
      if (foundDay) {
        currentDay = foundDay;
        continue;
      } else {
        currentDay = null;
      }
    }

    // 提取核心目标
    if (inCoreGoals) {
      const mBold = line.match(/^\d+\.\s*\*\*(.+?)\*\*/);
      if (mBold) {
        coreGoals.push(mBold[1].trim());
      } else {
        const mPlain = line.match(/^\d+\.\s*(.+)/);
        if (mPlain) coreGoals.push(mPlain[1].trim());
      }
    }

    // 提取当天任务（- [ ] / - [x]）
    if (currentDay) {
      const m = line.match(/^-\s*\[([x\s])\]\s*(.+)$/);
      if (m) {
        dayTasks[currentDay].push({
          content: m[2].trim(),
          completed: m[1] === 'x',
        });
      }
    }
  }

  // 将英文天名映射到中文天名（合并）
  for (let i = 0; i < dayNamesAlt.length; i++) {
    const altTasks = dayTasks[dayNamesAlt[i]];
    if (altTasks && altTasks.length > 0) {
      dayTasks[dayNames[i]] = [...dayTasks[dayNames[i]], ...altTasks];
    }
    delete dayTasks[dayNamesAlt[i]];
  }

  return { coreGoals, dayTasks };
}

// ============================================
// 默认目标（当 Obsidian 中没有年度目标文件时）
// 白皮书铁律二：禁止 mock 假数据，无数据时返回 null
// ============================================

function getDefaultGoals(): AnnualGoalData | null {
  // 禁止 mock 假数据，无数据时返回 null
  return null;
}
